import { apolloClient } from '@/app/providers/apollo';
import type { RoleName, UserStatus } from '../lib/admin.schema';
import {
  AdminAddOrgMemberDocument,
  AdminCreateOrganizationDocument,
  AdminCreateUserDocument,
  AdminDeleteOrganizationDocument,
  AdminDeleteUserDocument,
  AdminListOrganizationsDocument,
  AdminListUsersDocument,
  AdminRemoveOrgMemberDocument,
  AdminUpdateUserDocument,
  AdminUserStatsDocument,
  ManagerAddOrgMemberDocument,
  ManagerCreateOrganizationDocument,
  ManagerCreateUserDocument,
  ManagerDeleteOrganizationDocument,
  ManagerDeleteUserDocument,
  ManagerListOrganizationsDocument,
  ManagerListUsersDocument,
  ManagerRemoveOrgMemberDocument,
  ManagerUpdateUserDocument,
  ManagerUserStatsDocument,
} from '../__generated__/graphql';

export interface ListUsersParams {
  page?: number;
  limit?: number;
  status?: UserStatus;
}

export interface ListAuditLogsParams {
  action?: string;
  email?: string;
  page?: number;
  limit?: number;
}

export interface CreateUserInput {
  email: string;
  password?: string | undefined;
  organization: string;
  display_name?: string;
  phone?: string | null;
  role?: RoleName;
}

export interface UpdateUserInput {
  email: string;
  display_name?: string | null;
  new_email?: string;
  password?: string;
  phone?: string | null;
  status?: UserStatus;
  role?: RoleName;
}

export interface Organization {
  slug: string;
  name: string;
  description: string | null;
  created_at: string;
}

export interface CreateOrganizationInput {
  name: string;
  slug: string;
  description?: string;
}

const ADMIN_OPS = {
  createUser: AdminCreateUserDocument,
  updateUser: AdminUpdateUserDocument,
  deleteUser: AdminDeleteUserDocument,
  createOrganization: AdminCreateOrganizationDocument,
  deleteOrganization: AdminDeleteOrganizationDocument,
  addOrgMember: AdminAddOrgMemberDocument,
  removeOrgMember: AdminRemoveOrgMemberDocument,
};

const MANAGER_OPS: typeof ADMIN_OPS = {
  createUser: ManagerCreateUserDocument,
  updateUser: ManagerUpdateUserDocument,
  deleteUser: ManagerDeleteUserDocument,
  createOrganization: ManagerCreateOrganizationDocument,
  deleteOrganization: ManagerDeleteOrganizationDocument,
  addOrgMember: ManagerAddOrgMemberDocument,
  removeOrgMember: ManagerRemoveOrgMemberDocument,
};

type ManagementOps = typeof ADMIN_OPS;

function createManagementApi(ops: ManagementOps) {
  return {
    createUser: (input: CreateUserInput) =>
      apolloClient.mutate({ mutation: ops.createUser, variables: { input } }).then((result) => {
        const payload = result.data?.createUser;
        if (!payload) {
          throw new Error('Create user failed');
        }
        return payload;
      }),

    updateUser: (input: UpdateUserInput) =>
      apolloClient.mutate({ mutation: ops.updateUser, variables: { input } }).then((result) => {
        const payload = result.data?.updateUser;
        if (!payload) {
          throw new Error('Update user failed');
        }
        return payload;
      }),

    deleteUser: (email: string) =>
      apolloClient
        .mutate({ mutation: ops.deleteUser, variables: { input: { email } } })
        .then((result) => {
          const payload = result.data?.deleteUser;
          if (!payload) {
            throw new Error('Delete user failed');
          }
          return payload;
        }),

    setRole: (email: string, role: RoleName) =>
      apolloClient
        .mutate({ mutation: ops.updateUser, variables: { input: { email, role } } })
        .then((result) => {
          const payload = result.data?.updateUser;
          if (!payload) {
            throw new Error('Set role failed');
          }
          return payload;
        }),

    createOrganization: (input: CreateOrganizationInput) =>
      apolloClient
        .mutate({ mutation: ops.createOrganization, variables: { input } })
        .then((result) => {
          const payload = result.data?.createOrganization;
          if (!payload) {
            throw new Error('Create organization failed');
          }
          return payload;
        }),

    deleteOrganization: (slug: string) =>
      apolloClient
        .mutate({ mutation: ops.deleteOrganization, variables: { slug } })
        .then((result) => {
          const payload = result.data?.deleteOrganization;
          if (!payload) {
            throw new Error('Delete organization failed');
          }
          return payload;
        }),

    addOrgMember: (slug: string, email: string) =>
      apolloClient
        .mutate({ mutation: ops.addOrgMember, variables: { input: { slug, email } } })
        .then((result) => {
          const payload = result.data?.addOrgMember;
          if (!payload) {
            throw new Error('Add org member failed');
          }
          return payload;
        }),

    removeOrgMember: (slug: string, email: string) =>
      apolloClient
        .mutate({ mutation: ops.removeOrgMember, variables: { input: { slug, email } } })
        .then((result) => {
          const payload = result.data?.removeOrgMember;
          if (!payload) {
            throw new Error('Remove org member failed');
          }
          return payload;
        }),
  };
}

export const adminApi = createManagementApi(ADMIN_OPS);
export const managerApi = createManagementApi(MANAGER_OPS);
export type ManagementApi = ReturnType<typeof createManagementApi>;

// Apollo matches by operation document, so including both admin and manager variants reaches
// whichever list/stats/orgs query is currently mounted for the caller's role — the analogue of
// the old TanStack ['admin', 'users'] / ['admin', 'organizations'] prefix invalidate. Awaited so
// a refetch failure lands in the caller's try/catch.
export const refetchUsers = () =>
  apolloClient.refetchQueries({
    include: [
      AdminListUsersDocument,
      ManagerListUsersDocument,
      AdminUserStatsDocument,
      ManagerUserStatsDocument,
    ],
  });

export const refetchOrganizations = () =>
  apolloClient.refetchQueries({
    include: [AdminListOrganizationsDocument, ManagerListOrganizationsDocument],
  });
